import { type CSSProperties, useRef, useCallback, useState } from 'react';
import { GripVertical, Pencil, Trash2, MoreVertical, Plus } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import type { TodoItemResponse } from '@/lib/api';
import { AssignmentBadge } from './AssignmentBadge';
import { AssignUserButton } from './AssignUserButton';
import { useTodoListContext } from '../context';

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-muted text-muted-foreground' },
  in_progress: {
    label: 'In Progress',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
};

interface TodoItemRowProps {
  item: TodoItemResponse;
  /** Override for canEdit — when omitted, falls back to isOwner from context */
  canEdit?: boolean;
}

export function TodoItemRow({ item, canEdit }: TodoItemRowProps) {
  const {
    isOwner,
    collaborators,
    itemAssignments,
    onToggleItem: onToggle,
    onEditItem: onEdit,
    onDeleteItem: onDelete,
    onUpdateItemTitle: onUpdateTitle,
    onCreateItemBelow,
    onAssignItem,
    onUnassignItem,
  } = useTodoListContext();

  const assignments = itemAssignments.filter((a) => a.itemId === item.itemId);
  const effectiveCanEdit = canEdit ?? isOwner;
  const isCompleted = item.status === 'completed';
  const statusStyle = STATUS_STYLES[item.status] ?? STATUS_STYLES.pending;
  const titleRef = useRef<HTMLDivElement>(null);
  const addBelowRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [addBelowActive, setAddBelowActive] = useState(false);
  const [addBelowTitle, setAddBelowTitle] = useState('');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.itemId,
    disabled: !isOwner,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const saveTitle = useCallback(() => {
    if (!titleRef.current) return;
    const newTitle = titleRef.current.innerText?.trim() ?? '';
    if (newTitle && newTitle !== item.title) {
      onUpdateTitle?.(item.itemId, newTitle);
    } else if (!newTitle) {
      titleRef.current.innerText = item.title;
    }
  }, [item.itemId, item.title, onUpdateTitle]);

  return (
    <div ref={setNodeRef} style={style}>
      <div className="group flex items-start gap-2 rounded-md px-3 py-2 hover:bg-muted/50 transition-colors">
        {isOwner && (
          <button
            type="button"
            className="mt-0.5 cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        {effectiveCanEdit ? (
          <Checkbox
            checked={isCompleted}
            onCheckedChange={() => onToggle?.(item)}
            className="mt-0.5"
            aria-label={`Mark "${item.title}" as ${isCompleted ? 'pending' : 'completed'}`}
          />
        ) : !isOwner ? (
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-white flex-shrink-0" />
        ) : null}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div
              ref={titleRef}
              contentEditable={effectiveCanEdit}
              suppressContentEditableWarning
              className={`text-sm outline-none whitespace-pre-wrap ${
                isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'
              } ${effectiveCanEdit ? 'cursor-text focus:ring-1 focus:ring-primary/30 focus:rounded-sm focus:px-1 focus:-mx-1' : ''}`}
              onFocus={() => setIsEditing(true)}
              onBlur={() => {
                saveTitle();
                setIsEditing(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  (e.target as HTMLElement).blur();
                }
                if (e.key === 'Escape') {
                  if (titleRef.current) titleRef.current.innerText = item.title;
                  (e.target as HTMLElement).blur();
                }
              }}
            >
              {item.title}
            </div>
            {item.status !== 'pending' && (
              <Badge
                variant="secondary"
                className={`text-[10px] px-1.5 py-0 border-0 ${statusStyle.className}`}
              >
                {statusStyle.label}
              </Badge>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>
          )}
        </div>

        {effectiveCanEdit && isEditing && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onMouseDown={(e) => {
              e.preventDefault();
              titleRef.current?.blur();
              setAddBelowActive(true);
              setTimeout(() => addBelowRef.current?.focus(), 0);
            }}
            aria-label="Add item below"
            title="Add item below"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}

        <AssignmentBadge assignments={assignments} />

        {isOwner && collaborators.length > 0 && (
          <AssignUserButton
            collaborators={collaborators}
            currentAssignments={assignments}
            onAssign={(username) => onAssignItem?.(item.itemId, username)}
            onUnassign={(uid) => onUnassignItem?.(item.itemId, uid)}
          />
        )}

        {(isOwner || effectiveCanEdit) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {effectiveCanEdit && (
                <DropdownMenuItem onClick={() => onEdit?.(item)}>
                  <Pencil className="h-3.5 w-3.5 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete?.(item.itemId)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {addBelowActive && (
        <div className="flex items-center gap-2 px-3 py-2">
          <Input
            ref={addBelowRef}
            value={addBelowTitle}
            onChange={(e) => setAddBelowTitle(e.target.value)}
            placeholder="New item title..."
            className="h-8 text-sm"
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && addBelowTitle.trim()) {
                const title = addBelowTitle.trim();
                setAddBelowActive(false);
                setAddBelowTitle('');
                await onCreateItemBelow?.(item.sectionId, item.itemId, title);
              }
              if (e.key === 'Escape') {
                setAddBelowActive(false);
                setAddBelowTitle('');
              }
            }}
            onBlur={() => {
              if (!addBelowTitle.trim()) {
                setAddBelowActive(false);
                setAddBelowTitle('');
              }
            }}
          />
        </div>
      )}
    </div>
  );
}
